import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Divider,
  Avatar,
  Stack,
  Paper,
} from '@mui/material';
import { GridLegacy as Grid } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Timeline as TimelineIcon,
  AccountBalance as AccountBalanceIcon,
  Percent as PercentIcon,
  Assessment as AssessmentIcon,
  Star as StarIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { PieChart, BarChart } from '@mui/x-charts';
import { Trade } from '../../contexts/TradingJournalContext';

interface TradingStatisticsProps {
  trades: Trade[];
}

const TradingStatistics: React.FC<TradingStatisticsProps> = ({ trades }) => {
  const closedTrades = trades.filter(trade => trade.status === 'closed' && trade.pnl !== undefined);
  const openTrades = trades.filter(trade => trade.status === 'open');
  
  // Calculate basic statistics
  const totalTrades = closedTrades.length;
  const winningTrades = closedTrades.filter(trade => (trade.pnl || 0) > 0);
  const losingTrades = closedTrades.filter(trade => (trade.pnl || 0) < 0);
  
  const totalPnL = closedTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0);
  const totalCommission = trades.reduce((sum, trade) => sum + trade.commission, 0);
  const totalSwap = trades.reduce((sum, trade) => sum + (trade.swap || 0), 0);
  
  const winRate = totalTrades > 0 ? (winningTrades.length / totalTrades) * 100 : 0;
  const winningPnL = winningTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0);
  const losingPnL = Math.abs(losingTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0));
  
  const averageWin = winningTrades.length > 0 ? winningPnL / winningTrades.length : 0;
  const averageLoss = losingTrades.length > 0 ? losingPnL / losingTrades.length : 0;
  const profitFactor = losingPnL > 0 ? winningPnL / losingPnL : winningPnL > 0 ? Infinity : 0;
  
  const largestWin = winningTrades.length > 0 ? Math.max(...winningTrades.map(t => t.pnl || 0)) : 0;
  const largestLoss = losingTrades.length > 0 ? Math.min(...losingTrades.map(t => t.pnl || 0)) : 0;
  
  // Calculate consecutive wins/losses
  let maxConsecutiveWins = 0;
  let maxConsecutiveLosses = 0;
  let currentWinStreak = 0;
  let currentLossStreak = 0;
  
  closedTrades.forEach(trade => {
    if ((trade.pnl || 0) > 0) {
      currentWinStreak++;
      currentLossStreak = 0;
      maxConsecutiveWins = Math.max(maxConsecutiveWins, currentWinStreak);
    } else {
      currentLossStreak++;
      currentWinStreak = 0;
      maxConsecutiveLosses = Math.max(maxConsecutiveLosses, currentLossStreak);
    }
  });
  
  // Broker distribution
  const brokerStats = trades.reduce((acc, trade) => {
    acc[trade.broker] = (acc[trade.broker] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  // Symbol performance
  const symbolStats = closedTrades.reduce((acc, trade) => {
    if (!acc[trade.symbol]) {
      acc[trade.symbol] = { trades: 0, pnl: 0, wins: 0 };
    }
    acc[trade.symbol].trades++;
    acc[trade.symbol].pnl += (trade.pnl || 0);
    if ((trade.pnl || 0) > 0) acc[trade.symbol].wins++;
    return acc;
  }, {} as Record<string, { trades: number; pnl: number; wins: number }>);
  
  // Strategy performance
  const strategyStats = closedTrades.reduce((acc, trade) => {
    const strategy = trade.strategy || 'Unknown';
    if (!acc[strategy]) {
      acc[strategy] = { trades: 0, pnl: 0, wins: 0 };
    }
    acc[strategy].trades++;
    acc[strategy].pnl += (trade.pnl || 0);
    if ((trade.pnl || 0) > 0) acc[strategy].wins++;
    return acc;
  }, {} as Record<string, { trades: number; pnl: number; wins: number }>);
  
  // Monthly performance
  const monthlyStats = closedTrades.reduce((acc, trade) => {
    const month = new Date(trade.entryTime).toISOString().slice(0, 7); // YYYY-MM
    if (!acc[month]) {
      acc[month] = { trades: 0, pnl: 0, wins: 0 };
    }
    acc[month].trades++;
    acc[month].pnl += (trade.pnl || 0);
    if ((trade.pnl || 0) > 0) acc[month].wins++;
    return acc;
  }, {} as Record<string, { trades: number; pnl: number; wins: number }>);

  const StatCard = ({ 
    title, 
    value, 
    subtitle, 
    icon, 
    color = 'primary',
    trend 
  }: {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    color?: 'primary' | 'success' | 'error' | 'warning' | 'info';
    trend?: 'up' | 'down' | 'neutral';
  }) => (
    <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
              {value}
            </Typography>
            <Typography variant="body1" color="text.primary" fontWeight="medium">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (totalTrades === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="text.secondary">
          No closed trades available for statistics
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Complete some trades to see your performance metrics
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Overview Stats */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Trades"
            value={totalTrades}
            subtitle={`${openTrades.length} open`}
            icon={<TimelineIcon />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Win Rate"
            value={`${winRate.toFixed(1)}%`}
            subtitle={`${winningTrades.length}W / ${losingTrades.length}L`}
            icon={<PercentIcon />}
            color={winRate >= 50 ? 'success' : 'error'}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total P&L"
            value={`$${totalPnL.toFixed(2)}`}
            subtitle={`Net: $${(totalPnL - totalCommission - totalSwap).toFixed(2)}`}
            icon={totalPnL >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
            color={totalPnL >= 0 ? 'success' : 'error'}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Profit Factor"
            value={profitFactor === Infinity ? '∞' : profitFactor.toFixed(2)}
            subtitle={profitFactor >= 1.5 ? 'Excellent' : profitFactor >= 1.2 ? 'Good' : 'Poor'}
            icon={<AssessmentIcon />}
            color={profitFactor >= 1.5 ? 'success' : profitFactor >= 1.0 ? 'warning' : 'error'}
          />
        </Grid>
      </Grid>

      {/* Detailed Statistics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Trade Performance
              </Typography>
              
              <Stack spacing={2}>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Average Win</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      ${averageWin.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Average Loss</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">
                      -${averageLoss.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Largest Win</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      ${largestWin.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Largest Loss</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">
                      ${largestLoss.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
                
                <Divider />
                
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Max Consecutive Wins</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {maxConsecutiveWins}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Max Consecutive Losses</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">
                      {maxConsecutiveLosses}
                    </Typography>
                  </Box>
                </Box>
                
                <Divider />
                
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Total Commission</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      ${totalCommission.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Total Swap</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      ${totalSwap.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Win/Loss Distribution
              </Typography>
              
              <Box display="flex" justifyContent="center" mb={2}>
                <PieChart
                  series={[
                    {
                      data: [
                        { id: 0, value: winningTrades.length, label: 'Wins', color: '#4caf50' },
                        { id: 1, value: losingTrades.length, label: 'Losses', color: '#f44336' },
                      ],
                    },
                  ]}
                  width={300}
                  height={200}
                />
              </Box>
              
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2">Winning Trades</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {winningTrades.length} ({((winningTrades.length / totalTrades) * 100).toFixed(1)}%)
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: 'error.main',
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2">Losing Trades</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {losingTrades.length} ({((losingTrades.length / totalTrades) * 100).toFixed(1)}%)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Performing Symbols */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Top Performing Symbols
              </Typography>
              
              <Stack spacing={2}>
                {Object.entries(symbolStats)
                  .sort(([,a], [,b]) => b.pnl - a.pnl)
                  .slice(0, 5)
                  .map(([symbol, stats]) => (
                    <Box key={symbol}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {symbol}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={stats.pnl >= 0 ? 'success.main' : 'error.main'}
                        >
                          {stats.pnl >= 0 ? '+' : ''}${stats.pnl.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {stats.trades} trades • {((stats.wins / stats.trades) * 100).toFixed(0)}% win rate
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(stats.wins / stats.trades) * 100}
                          sx={{ width: 60, ml: 1 }}
                          color={stats.wins / stats.trades >= 0.5 ? 'success' : 'error'}
                        />
                      </Box>
                    </Box>
                  ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Strategy Performance
              </Typography>
              
              <Stack spacing={2}>
                {Object.entries(strategyStats)
                  .sort(([,a], [,b]) => b.pnl - a.pnl)
                  .slice(0, 5)
                  .map(([strategy, stats]) => (
                    <Box key={strategy}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {strategy}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={stats.pnl >= 0 ? 'success.main' : 'error.main'}
                        >
                          {stats.pnl >= 0 ? '+' : ''}${stats.pnl.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {stats.trades} trades • {((stats.wins / stats.trades) * 100).toFixed(0)}% win rate
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(stats.wins / stats.trades) * 100}
                          sx={{ width: 60, ml: 1 }}
                          color={stats.wins / stats.trades >= 0.5 ? 'success' : 'error'}
                        />
                      </Box>
                    </Box>
                  ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Performance Chart */}
      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Monthly Performance
          </Typography>
          
          <Box sx={{ height: 300, width: '100%' }}>
            <BarChart
              dataset={Object.entries(monthlyStats).map(([month, stats]) => ({
                month,
                pnl: stats.pnl,
                trades: stats.trades,
              }))}
              xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
              series={[
                { 
                  dataKey: 'pnl', 
                  label: 'P&L ($)',
                  color: '#1976d2',
                },
              ]}
              width={undefined}
              height={250}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TradingStatistics; 